import { useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";
import { useTheme } from "../../contexts/ThemeContext";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://trackly.app";
const termsJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Terms of Service | Trackly",
  "description": "Trackly's terms of service. Read our terms on account usage, Pro subscriptions, acceptable use policies, and more.",
  "url": `${SITE_URL}/terms`,
  "isPartOf": { "@type": "WebSite", "name": "Trackly", "url": SITE_URL },
};

const SECTIONS = [
  {
    title: "Acceptance of Terms",
    icon: "ri-file-check-line",
    content: [
      {
        heading: "Agreement to Terms",
        body: "By accessing or using Trackly, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use the service.",
      },
      {
        heading: "Eligibility",
        body: "You must be at least 16 years old to use Trackly. By using the service, you represent and warrant that you meet this requirement and have the legal capacity to enter into a binding agreement.",
      },
      {
        heading: "Changes to Terms",
        body: "We may update these Terms from time to time. We will notify you of material changes via email or an in-app notice. Continued use of Trackly after changes take effect constitutes your acceptance of the updated Terms.",
      },
    ],
  },
  {
    title: "Your Account",
    icon: "ri-user-3-line",
    content: [
      {
        heading: "Account Registration",
        body: "To access most features, you must create an account using a valid email address. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.",
      },
      {
        heading: "Accurate Information",
        body: "You agree to provide accurate, current, and complete information during registration and to keep your account information up to date at all times.",
      },
      {
        heading: "Account Security",
        body: "You agree to notify us immediately at support@trackly.to if you suspect any unauthorised use of your account. We are not liable for any loss or damage arising from your failure to maintain adequate account security.",
      },
    ],
  },
  {
    title: "Acceptable Use",
    icon: "ri-checkbox-circle-line",
    content: [
      {
        heading: "Permitted Use",
        body: "Trackly is provided for personal and commercial use to track resale transactions, calculate margins, and manage flip profitability. You may use Trackly only for lawful purposes consistent with these Terms.",
      },
      {
        heading: "Prohibited Activities",
        body: "You must not: attempt to reverse-engineer or circumvent security measures; use automated scripts to scrape or overload the platform; share your account credentials with third parties; submit false or misleading data; or use Trackly for any illegal activity including tax evasion or money laundering.",
      },
      {
        heading: "Content You Submit",
        body: "You retain ownership of all data you enter into Trackly. By submitting data, you grant us a limited licence to store, process, and display it solely for the purpose of providing the service to you.",
      },
    ],
  },
  {
    title: "Subscription & Billing",
    icon: "ri-bank-card-line",
    content: [
      {
        heading: "Free & Pro Plans",
        body: "Trackly offers a free tier with limited features and a Pro plan with full access. Plan details, including feature availability and pricing, are described on the Product page and may be updated at our discretion.",
      },
      {
        heading: "Payments",
        body: "Pro subscriptions are billed via Stripe. By subscribing, you authorise us to charge your payment method on a recurring basis (monthly or as per your chosen plan). All charges are in USD unless stated otherwise.",
      },
      {
        heading: "Cancellation & Refunds",
        body: "You may cancel your Pro subscription at any time. Cancellation takes effect at the end of the current billing period — you will retain access until then. We do not offer refunds for partial billing periods except where required by applicable law.",
      },
      {
        heading: "Lifetime Access",
        body: "Lifetime plan purchases are one-time payments granting indefinite access to Pro features as they exist at the time of purchase and future feature updates. Lifetime plans are non-refundable after 14 days from purchase.",
      },
    ],
  },
  {
    title: "Intellectual Property",
    icon: "ri-copyright-line",
    content: [
      {
        heading: "Trackly Ownership",
        body: "All content, features, design, trademarks, and underlying technology of Trackly are owned by or licensed to Trackly and are protected by applicable intellectual property laws. You may not copy, reproduce, or distribute any part of the platform without our prior written consent.",
      },
      {
        heading: "Your Data",
        body: "You own all the data you input into Trackly. We claim no intellectual property rights over your flip records, profit entries, or any other content you create within the platform.",
      },
    ],
  },
  {
    title: "Disclaimers & Limitation of Liability",
    icon: "ri-error-warning-line",
    content: [
      {
        heading: "No Financial Advice",
        body: "Trackly is a data tracking and analytics tool. Nothing on Trackly constitutes financial, tax, legal, or investment advice. You are solely responsible for your own resale business decisions and tax obligations.",
      },
      {
        heading: "Service Availability",
        body: "We strive for high availability but do not guarantee that Trackly will be uninterrupted, error-free, or free of harmful components. The service is provided on an \"as is\" and \"as available\" basis.",
      },
      {
        heading: "Limitation of Liability",
        body: "To the maximum extent permitted by law, Trackly and its team shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service, including loss of profits or data, even if advised of the possibility of such damages.",
      },
    ],
  },
  {
    title: "Termination",
    icon: "ri-close-circle-line",
    content: [
      {
        heading: "Termination by You",
        body: "You may stop using Trackly and delete your account at any time via the dashboard settings or by contacting us.",
      },
      {
        heading: "Termination by Us",
        body: "We reserve the right to suspend or terminate your account if we reasonably believe you have violated these Terms, engaged in fraudulent activity, or pose a risk to other users or the platform.",
      },
      {
        heading: "Effect of Termination",
        body: "Upon termination, your right to use Trackly ceases immediately. We will delete your data in accordance with our Privacy Policy retention schedule.",
      },
    ],
  },
  {
    title: "Governing Law",
    icon: "ri-scales-3-line",
    content: [
      {
        heading: "Jurisdiction",
        body: "These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.",
      },
      {
        heading: "Severability",
        body: "If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.",
      },
    ],
  },
];

export default function Terms() {
  const { isDark } = useTheme();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-[#f8f8f8]"}`}>
      <SEOHead
        title="Terms of Service | Trackly — Reseller Profit Tracker"
        description="Trackly's terms of service. Read our terms on account usage, Pro subscriptions, acceptable use policies, refunds, and more."
        keywords="trackly terms of service, reseller app terms, profit tracker subscription terms"
        canonical={`${SITE_URL}/terms`}
        jsonLd={termsJsonLd}
      />
      <Navbar />

      <main className="pt-32 pb-24 px-8 lg:px-16">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="mb-16">
            <span className={`inline-flex items-center gap-2 font-inter text-xs font-medium px-3 py-1.5 rounded-full mb-6 ${isDark ? "bg-white/5 text-[#888]" : "bg-black/6 text-[#666]"}`}>
              <i className="ri-file-text-line text-xs" />
              Last updated March 20, 2026
            </span>
            <h1 className={`font-syne text-4xl font-bold mb-5 leading-tight ${isDark ? "text-white" : "text-[#111111]"}`}>
              Terms of Service
            </h1>
            <p className={`font-inter text-base leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
              These Terms govern your access to and use of Trackly. Please read them carefully before using the platform.
            </p>
          </div>

          {/* Sections */}
          <div className="flex flex-col gap-10">
            {SECTIONS.map((section, index) => (
              <section
                key={section.title}
                className={`rounded-2xl border p-8 transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}
              >
                <div className="flex items-center gap-3 mb-7">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-white/6" : "bg-black/5"}`}>
                    <i className={`${section.icon} text-base ${isDark ? "text-[#aaaaaa]" : "text-[#555555]"}`} />
                  </div>
                  <h2 className={`font-syne text-lg font-semibold ${isDark ? "text-white" : "text-[#111111]"}`}>
                    <span className={`font-inter text-sm mr-2 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>{String(index + 1).padStart(2, "0")}</span>
                    {section.title}
                  </h2>
                </div>
                <div className="flex flex-col gap-6">
                  {section.content.map((item) => (
                    <div key={item.heading}>
                      <h3 className={`font-inter text-sm font-semibold mb-2 ${isDark ? "text-[#cccccc]" : "text-[#222222]"}`}>
                        {item.heading}
                      </h3>
                      <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                        {item.body}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Refund Policy */}
          <section className={`mt-10 rounded-2xl border p-8 transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}>
            <div className="flex items-center gap-3 mb-7">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-white/6" : "bg-black/5"}`}>
                <i className={`ri-refund-2-line text-base ${isDark ? "text-[#aaaaaa]" : "text-[#555555]"}`} />
              </div>
              <h2 className={`font-syne text-lg font-semibold ${isDark ? "text-white" : "text-[#111111]"}`}>
                Refund Policy
              </h2>
            </div>
            <div className="flex flex-col gap-6">
              <div>
                <h3 className={`font-inter text-sm font-semibold mb-2 ${isDark ? "text-[#cccccc]" : "text-[#222222]"}`}>
                  All Sales Are Final
                </h3>
                <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                  All purchases made through Trackly are final. Due to the nature of digital products, we do not offer refunds, returns, or exchanges once access has been granted.
                </p>
              </div>
              <div>
                <h3 className={`font-inter text-sm font-semibold mb-2 ${isDark ? "text-[#cccccc]" : "text-[#222222]"}`}>
                  Exceptions & Disputes
                </h3>
                <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                  If you experience a technical issue or believe you were charged incorrectly, please contact us at{" "}
                  <a
                    href="mailto://hello@trackly.to"
                    className={`font-medium underline underline-offset-2 transition-colors duration-200 ${isDark ? "text-[#aaaaaa] hover:text-white" : "text-[#333333] hover:text-[#111111]"}`}
                  >
                    hello@trackly.to
                  </a>{" "}
                  and we will review your case.
                </p>
              </div>
              <div>
                <h3 className={`font-inter text-sm font-semibold mb-2 ${isDark ? "text-[#cccccc]" : "text-[#222222]"}`}>
                  Discretionary Refunds
                </h3>
                <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                  We reserve the right to issue refunds at our sole discretion on a case-by-case basis.
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <div className={`mt-10 rounded-2xl border p-8 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}>
            <h2 className={`font-syne text-lg font-semibold mb-3 ${isDark ? "text-white" : "text-[#111111]"}`}>
              Questions About These Terms?
            </h2>
            <p className={`font-inter text-sm leading-relaxed mb-5 ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
              If you have any questions or concerns about these Terms of Service, reach out to us directly:
            </p>
            <a
              href="mailto://support@trackly.to"
              className={`inline-flex items-center gap-2 font-inter text-sm font-medium cursor-pointer transition-colors duration-200 ${isDark ? "text-white hover:text-[#cccccc]" : "text-[#111111] hover:text-[#444444]"}`}
            >
              <i className="ri-mail-line" />
              support@trackly.to
            </a>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
